import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Headset, Send, Bot, Check, AlertTriangle, Wrench, Clock, Shield } from "lucide-react";
import { safeFormat } from "@/lib/dateUtils";
import { AIChatBubble } from "@/components/AIContentRenderer";

const QUICK_COMMANDS = [
  "Run system health check",
  "Check my latest inventory invoices and confirm they were posted to Expenses correctly",
  "Show me failed emails in the last 24 hours",
  "Verify the inventory system is correctly posting expenses",
  "Check if any automation emails failed today",
  "Why did my expense charts not update after uploading invoices?",
];

export default function HelpDesk() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Welcome. I'm Beyond AI — your help desk assistant. I can investigate system issues, check database records, diagnose email problems, and help fix data inconsistencies.\n\nTry asking me something like:\n- Check my latest invoices\n- Show failed emails\n- Run system health check" }
  ]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/helpdesk/chat`, { message: msg, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        actions: res.data.actions || []
      }]);
      // If there are suggested actions, prompt
      if (res.data.actions?.length > 0) {
        setPendingAction(res.data.actions[0]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Beyond AI encountered an issue. Please try again.' }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const executeAction = async (action) => {
    try {
      const res = await axios.post(`${API}/helpdesk/execute-action`, action);
      if (res.data.success) {
        toast.success(res.data.message);
        setMessages(prev => [...prev, { role: 'system', content: `✅ Action completed: ${res.data.message}` }]);
      } else {
        toast.error(res.data.message);
        setMessages(prev => [...prev, { role: 'system', content: `❌ Action failed: ${res.data.message}` }]);
      }
    } catch (e) {
      toast.error('Action failed');
    }
    setPendingAction(null);
  };

  const loadLogs = async () => {
    try {
      const res = await axios.get(`${API}/helpdesk/logs?limit=30`);
      setLogs(res.data.logs || []);
      setShowLogs(true);
    } catch (e) {  }
  };

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
            <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-7 h-7 rounded-xl object-cover" />
            Beyond <span style={{ color: '#2DD4BF', fontWeight: 700 }}>AI</span> · help desk
          </h1>
          <p className="text-sm text-muted-foreground">Investigate issues, diagnose problems, and fix data</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadLogs} className="rounded-xl">
          <Clock size={14} className="mr-1" /> Action Log
        </Button>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Chat panel */}
        <Card className="lg:col-span-3 rounded-2xl border shadow-sm overflow-hidden flex flex-col" style={{ height: '600px' }}>
          <div className="p-3 border-b bg-muted/30 flex items-center gap-2">
            <Shield size={16} className="text-primary" />
            <span className="text-xs font-medium">System Diagnostics & Investigation</span>
            <Badge variant="outline" className="text-[9px] rounded-full ml-auto">Safe mode — confirms before changes</Badge>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-xl overflow-hidden mr-2 shrink-0 mt-1">
                      <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {msg.role === 'system' && (
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center mr-2 shrink-0 mt-1">
                      <Wrench size={14} className="text-green-600" />
                    </div>
                  )}
                  <div className={`max-w-[85%] p-3 leading-relaxed ${
                    msg.role === 'user' ? 'chat-bubble-user text-sm' :
                    msg.role === 'system' ? 'bg-green-50 border border-green-200 rounded-2xl text-sm' :
                    'chat-bubble-assistant'
                  }`}>
                    {msg.role === 'assistant' ? <AIChatBubble content={msg.content} /> : msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-xl overflow-hidden mr-2 shrink-0">
                    <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-full h-full object-cover" />
                  </div>
                  <div className="chat-bubble-assistant p-3">
                    <div className="flex gap-1 items-center text-xs text-muted-foreground">
                      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{animationDelay:'0.15s'}} />
                      <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{animationDelay:'0.3s'}} />
                      <span className="ml-2">Investigating...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          {/* Action confirmation bar */}
          {pendingAction && (
            <div className="p-3 border-t bg-yellow-50 border-yellow-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs">
                <AlertTriangle size={14} className="text-yellow-600" />
                <span className="font-medium text-yellow-800">Action suggested: {pendingAction.type.replace('_', ' ')}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPendingAction(null)} className="rounded-lg h-7 text-xs">Dismiss</Button>
                <Button size="sm" onClick={() => executeAction(pendingAction)} className="rounded-lg h-7 text-xs bg-green-600 hover:bg-green-700">
                  <Check size={12} className="mr-1" /> Execute
                </Button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t bg-card">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Describe the issue or ask a question..."
                className="rounded-xl"
              />
              <Button size="icon" onClick={() => sendMessage()} disabled={loading} className="rounded-xl shrink-0">
                <Send size={16} />
              </Button>
            </div>
          </div>
        </Card>

        {/* Quick Commands */}
        <div className="space-y-3">
          <Card className="p-4 rounded-2xl border shadow-sm">
            <h3 className="text-xs font-semibold mb-3" style={{ fontFamily: 'Playfair Display' }}>Quick Commands</h3>
            <div className="space-y-1.5">
              {QUICK_COMMANDS.map((cmd, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(cmd)}
                  className="w-full text-left p-2 text-[11px] bg-muted/50 hover:bg-primary/10 rounded-xl transition-colors leading-tight"
                >
                  {cmd}
                </button>
              ))}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl border shadow-sm">
            <h3 className="text-xs font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>What I can do</h3>
            <div className="space-y-1.5 text-[10px] text-muted-foreground">
              <p>• Inspect invoices, expenses, emails</p>
              <p>• Diagnose posting & sending failures</p>
              <p>• Create missing expense records</p>
              <p>• Resend confirmation emails</p>
              <p>• Run system health checks</p>
              <p>• Search by client, invoice #, date</p>
            </div>
          </Card>
          <Button variant="outline" className="w-full rounded-xl text-xs" onClick={() => {
            setMessages([{ role: 'assistant', content: "Session reset. Beyond AI is here — how can I help?" }]);
            setSessionId(null);
          }}>New Session</Button>
        </div>
      </div>

      {/* Action Log Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}>Help Desk Action Log</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {logs.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No actions recorded yet</p> : 
              logs.map(l => (
                <div key={l.id} className="p-3 bg-muted/30 rounded-xl text-xs">
                  <p className="font-medium">{l.action || l.user_message?.substring(0, 80) || 'Chat'}</p>
                  <p className="text-muted-foreground mt-0.5">{safeFormat(l.created_at, 'd MMM yyyy HH:mm')}</p>
                </div>
              ))
            }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
